<template>
  <BaseModal :title="title" @on-close-modal="onCloseModal">
    <div class="modal-contacts">
      <p class="modal-contacts__text">
        {{ description }}
      </p>
      <MyForm
        id="get-contact"
        name="get-contact"
        class="modal-contacts__form"
        :schema="schema"
        :state="form"
        @submit="onSubmit"
      >
        <MyInput
          v-model.trim="form.name"
          name="name"
          autocomplete="given-name"
          caption="@Name"
        />
        <MyInput
          v-model.trim="form.phone"
          v-maska="'+38(0##) ### ## ##'"
          name="phone"
          autocomplete="phone"
          caption="@Phone number"
        />
        <MyInput
          v-model.trim="form.email"
          name="email"
          type="email"
          autocomplete="email"
          caption="E-mail"
        />
        <MyCheckbox
          v-model="form.isAgreementAccepted"
          name="isAgreementAccepted"
        >
          <span>
            {{ _T("@I agree with") }}
            <MyLink name="privacy-policy" target="_blank">
              {{ _T("@privacy policy") }}
            </MyLink>
          </span>
        </MyCheckbox>
        <ButtonPrimary
          type="submit"
          :disabled="isHandled"
          :aria-label="_T('@Send')"
          button-width="363px"
        >
          {{ _T("@Send") }}
        </ButtonPrimary>
      </MyForm>
    </div>
  </BaseModal>
</template>

<script setup>
import { vMaska } from "maska/vue";
import {
  successfulModal,
  useModalStore,
  getContactModal,
} from "~/modules/shared/modals/store/modalStore";
import { formatPhone, phoneCheck } from "~~/utils/phoneHelper";
import { useUserStore } from "~/store/userStore";
import { useAPI } from "~/uses/useMyFetch";
import BaseModal from "~/modules/shared/modals/BaseModal.vue";
import ButtonPrimary from "~/modules/shared/buttons/ButtonPrimary.vue";
import { useSingletonHandler } from "~/uses/useSingletonHandler";
import { z } from "zod";
import MyInput from "~/nuxt_modules/common/ui/components/form/MyInput.vue";
import MyCheckbox from "~/nuxt_modules/common/ui/components/form/MyCheckbox.vue";
import MyForm from "~/nuxt_modules/common/ui/components/form/MyForm.vue";

const modalStore = useModalStore();
const { getExtra } = storeToRefs(modalStore);

const userStore = useUserStore();
const { currentUser: user } = storeToRefs(userStore);

onMounted(() => {
  if (null !== user.value) {
    form.name = user.value.name.firstName;
    form.phone = user.value.phone;
    form.email = user.value.email;
  }
});

const { title, description, marketingEvent } = getExtra.value(getContactModal);

const successfulData = {
  title: _T("@Successful modal title"),
  description: _T("@Successful modal text"),
  isWithTimeout: true,
};

const schema = z.object({
  name: z
    .string(_T("@Entry field required"))
    .max(255, `${_T("@Maximum field length")} 255`)
    .regex(/^\S*$/, _T("@Must not contain spaces")),
  phone: z
    .string(_T("@Entry field required"))
    .refine(
      (phone) => phoneCheck(phone),
      _T("@You entered an incorrect phone number"),
    ),
  email: z
    .email(_T("@You entered an incorrect email"))
    .max(254, `${_T("@Maximum field length")} 254`),
  isAgreementAccepted: z.literal(
    true,
    _T("@You must accept the terms and conditions"),
  ),
});

const form = reactive({
  name: undefined,
  phone: undefined,
  email: undefined,
  isAgreementAccepted: true,
});

const { isHandled, handle: onSubmit } = useSingletonHandler(async (data) => {
  await useAPI(`/marketing/${marketingEvent.name}`, {
    method: "POST",
    body: {
      name: data.name,
      email: data.email,
      phone: formatPhone(data.phone),
      isConfirmed: data.isAgreementAccepted,
      ...marketingEvent.options,
    },
  }).then(() => {
    modalStore.toggleModal(getContactModal);

    modalStore.toggleModal(successfulModal, successfulData);
  });
});

const onCloseModal = () => {
  modalStore.toggleModal(getContactModal);
};
</script>

<style lang="scss" scoped>
.modal-contacts {
  @include flex-container(column, flex-start);
  gap: 24px;

  &__text {
    @include font;
    text-align: center;

    @include mobile {
      @include font(14, 18);
    }
  }

  &__form {
    width: 100%;
    @include flex-container(column, center, center);

    gap: 24px;
  }
}
</style>
